import { downloadTravelReportPdf } from '@/lib/travel-report-export';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  FileText,
  TrendingUp,
  Building2,
  Users,
  Truck,
  CheckCircle,
  AlertCircle,
  Banknote,
  Receipt,
  Handshake,
  PieChart,
  Scale,
  Download,
  Percent,
  Printer,
  BookOpen,
  ListOrdered,
  BedDouble,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { downloadXlsx, dateStamp } from '@/lib/download-xlsx';
import { showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';
import { WithholdingReport } from '@/components/accounting/withholding-report';
import { LedgerBookReport } from '@/components/accounting/ledger-book-report';
import { JournalBookReport } from '@/components/accounting/journal-book-report';
import { AccommodationTaxReport } from '@/components/accounting/accommodation-tax-report';
import { UnbalancedEntriesReport } from '@/components/accounting/unbalanced-entries-report';
import { ReportExportButton } from '@/components/accounting/report-export-button';
import {
  MizanPeriodPicker,
  defaultMizanPeriod,
  mizanPeriodToDates,
} from '@/components/reports/mizan-period-picker';

type ReportTab =
  | 'trial-balance'
  | 'profit-loss'
  | 'balance-sheet'
  | 'ar-aging'
  | 'ap-aging'
  | 'cash-flow'
  | 'kdv'
  | 'withholding'
  | 'ledger'
  | 'cash-book'
  | 'journal'
  | 'accommodation-tax'
  | 'checks'
  | 'commission'
  | 'profit-margin'
  | 'bank-recon';

const TABS: { id: ReportTab; labelKey: string; icon: React.ElementType }[] = [
  { id: 'trial-balance', labelKey: 'reports.tabs.trialBalance', icon: FileText },
  { id: 'profit-loss', labelKey: 'reports.tabs.profitLoss', icon: TrendingUp },
  { id: 'balance-sheet', labelKey: 'reports.tabs.balanceSheet', icon: Building2 },
  { id: 'ar-aging', labelKey: 'reports.tabs.arAging', icon: Users },
  { id: 'ap-aging', labelKey: 'reports.tabs.apAging', icon: Truck },
  { id: 'cash-flow', labelKey: 'reports.tabs.cashFlow', icon: Banknote },
  { id: 'kdv', labelKey: 'reports.tabs.kdv', icon: Receipt },
  { id: 'withholding', labelKey: 'reports.tabs.withholding', icon: Percent },
  { id: 'ledger', labelKey: 'sidebar:travelLedger', icon: BookOpen },
  { id: 'cash-book', labelKey: 'sidebar:travelCashBook', icon: Banknote },
  { id: 'journal', labelKey: 'reports.tabs.journal', icon: ListOrdered },
  { id: 'accommodation-tax', labelKey: 'reports.tabs.accommodationTax', icon: BedDouble },
  { id: 'checks', labelKey: 'reports.tabs.checks', icon: ShieldCheck },
  { id: 'commission', labelKey: 'reports.tabs.commission', icon: Handshake },
  { id: 'profit-margin', labelKey: 'reports.tabs.profitMargin', icon: PieChart },
  { id: 'bank-recon', labelKey: 'reports.tabs.bankRecon', icon: Scale },
];

export function ReportsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('accounting');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as ReportTab) || 'trial-balance';

  const setActiveTab = (tab: ReportTab) => {
    setSearchParams({ tab });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/travel/accounting')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{t('reports.title')}</h1>
          <p className="text-muted-foreground">{t('reports.subtitle')}</p>
        </div>
        <Button
          variant="outline"
          className="ml-auto h-8 print:hidden"
          onClick={() => window.print()}
        >
          <Printer className="mr-2 h-4 w-4" />
          {t('reports.print')}
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b print:hidden">
        <nav className="flex gap-4 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground border-transparent'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {t(tab.labelKey)}
            </button>
          ))}
        </nav>
      </div>

      {/* Report Content */}
      {activeTab === 'trial-balance' && <TrialBalanceReport />}
      {activeTab === 'profit-loss' && <ProfitLossReport />}
      {activeTab === 'balance-sheet' && <BalanceSheetReport />}
      {activeTab === 'ar-aging' && <ARAgingReport />}
      {activeTab === 'ap-aging' && <APAgingReport />}
      {activeTab === 'cash-flow' && <CashFlowReport />}
      {activeTab === 'kdv' && <KDVReport />}
      {activeTab === 'withholding' && <WithholdingReport app="TRAVEL" />}
      {activeTab === 'ledger' && <LedgerBookReport key="ledger" app="TRAVEL" />}
      {activeTab === 'cash-book' && <LedgerBookReport key="cash-book" app="TRAVEL" cashOnly />}
      {activeTab === 'journal' && <JournalBookReport app="TRAVEL" />}
      {activeTab === 'accommodation-tax' && <AccommodationTaxReport />}
      {activeTab === 'checks' && <UnbalancedEntriesReport />}
      {activeTab === 'commission' && <CommissionReport />}
      {activeTab === 'profit-margin' && <ProfitMarginReport />}
      {activeTab === 'bank-recon' && <BankReconciliationReport />}
    </div>
  );
}

// Trial Balance Report — legacy-style four-column layout:
// period turnover (Borç/Alacak) + closing balance (Borç Bakiye/Alacak Bakiye).
function TrialBalanceReport() {
  const { t, i18n } = useTranslation('accounting');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const [period, setPeriod] = useSessionState(
    'travel:accounting-reports:trialBalance:period',
    defaultMizanPeriod()
  );
  const [includeZero, setIncludeZero] = useSessionState(
    'travel:accounting-reports:trialBalance:includeZero',
    false
  );
  const [codeFrom, setCodeFrom] = useSessionState(
    'travel:accounting-reports:trialBalance:codeFrom',
    ''
  );
  const [codeTo, setCodeTo] = useSessionState('travel:accounting-reports:trialBalance:codeTo', '');
  const [level, setLevel] = useSessionState('travel:accounting-reports:trialBalance:level', ''); // '' = full detail
  const [exporting, setExporting] = useState(false);

  const { start, end } = useMemo(() => mizanPeriodToDates(period), [period]);

  const queryInput = {
    startDate: start,
    endDate: end,
    includeZeroBalances: includeZero,
    accountCodeFrom: codeFrom.trim() || undefined,
    accountCodeTo: codeTo.trim() || undefined,
    level: level ? Number(level) : undefined,
  };

  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.financialReports.getTrialBalance.useQuery(queryInput);

  const onExport = async (kind: 'xlsx' | 'pdf') => {
    setExporting(true);
    try {
      const res = await utils.financialReports.exportTrialBalance.fetch(queryInput);
      if (kind === 'pdf')
        await downloadTravelReportPdf(
          res.rows,
          'Mizan',
          [
            `${start?.toLocaleDateString(dateLocale) ?? ''} – ${end.toLocaleDateString(dateLocale)}`,
            `${codeFrom} – ${codeTo}`,
            level ? `Seviye: ${level}` : '',
          ],
          `mizan-${dateStamp()}.pdf`
        );
      else downloadXlsx(res.rows, 'Mizan', `mizan-${dateStamp()}.xlsx`);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const typeColors: Record<string, string> = {
    ASSET: 'text-blue-600',
    LIABILITY: 'text-red-600',
    EQUITY: 'text-purple-600',
    INCOME: 'text-green-600',
    EXPENSE: 'text-orange-600',
  };

  const num = (n: number) => (Math.abs(n) < 0.005 ? '-' : formatCurrency(n, 'TRY'));

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-card space-y-4 rounded-lg border p-4">
        <MizanPeriodPicker
          value={period}
          onChange={setPeriod}
          ns="accounting"
          keyPrefix="reports"
        />
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <Label>{t('reports.accountCodeFrom')}</Label>
            <Input
              type="text"
              value={codeFrom}
              onChange={(e) => setCodeFrom(e.target.value)}
              placeholder="100"
              className="w-36"
            />
          </div>
          <div className="space-y-2">
            <Label>{t('reports.accountCodeTo')}</Label>
            <Input
              type="text"
              value={codeTo}
              onChange={(e) => setCodeTo(e.target.value)}
              placeholder="799"
              className="w-36"
            />
          </div>
          <div className="space-y-2">
            <Label>{t('reports.level')}</Label>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="border-input bg-background block h-10 w-48 rounded-md border px-3 text-sm"
            >
              <option value="">{t('reports.levelDetail')}</option>
              <option value="1">{t('reports.level1')}</option>
              <option value="2">{t('reports.level2')}</option>
              <option value="3">{t('reports.level3')}</option>
            </select>
          </div>
          <div className="flex items-center gap-2 pb-2.5">
            <input
              type="checkbox"
              id="includeZero"
              checked={includeZero}
              onChange={(e) => setIncludeZero(e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="includeZero">{t('reports.includeZero')}</Label>
          </div>
          <div className="ml-auto flex gap-2 pb-1">
            <Button
              variant="outline"
              onClick={() => onExport('pdf')}
              disabled={exporting || !data?.accounts.length}
            >
              PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => onExport('xlsx')}
              disabled={exporting || !data?.accounts.length}
            >
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Excel
            </Button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : data ? (
        <div className="bg-card rounded-xl border">
          <div className="flex items-center justify-between border-b p-4">
            <div>
              <h3 className="text-lg font-semibold">{t('reports.trialBalance')}</h3>
              <p className="text-muted-foreground text-sm">
                {data.startDate
                  ? `${new Date(data.startDate).toLocaleDateString(dateLocale)} - ${new Date(data.endDate).toLocaleDateString(dateLocale)}`
                  : t('reports.asOfValue', {
                      date: new Date(data.endDate).toLocaleDateString(dateLocale),
                    })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {data.totals.isBalanced ? (
                <span className="flex items-center gap-1 text-sm text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  {t('reports.balanced')}
                </span>
              ) : (
                <span className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {t('reports.outOfBalance')}
                </span>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('reports.table.code')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('reports.table.accountName')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('reports.table.type')}
                  </th>
                  {data.hasOpening && (
                    <>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.openingDebit')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.openingCredit')}
                      </th>
                    </>
                  )}
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('reports.table.debit')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('reports.table.credit')}
                  </th>
                  {data.hasOpening && (
                    <>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.totalDebit')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.totalCredit')}
                      </th>
                    </>
                  )}
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('reports.debitBalance')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('reports.creditBalance')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.accounts.map((account) => (
                  <tr key={account.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 font-mono text-sm">{account.code}</td>
                    <td className="px-4 py-3">{account.name}</td>
                    <td className={`px-4 py-3 text-sm ${typeColors[account.type]}`}>
                      {account.type}
                    </td>
                    {data.hasOpening && (
                      <>
                        <td className="px-4 py-3 text-right font-mono">
                          {num(account.openingDebit)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {num(account.openingCredit)}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 text-right font-mono">{num(account.debitTurnover)}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      {num(account.creditTurnover)}
                    </td>
                    {data.hasOpening && (
                      <>
                        <td className="px-4 py-3 text-right font-mono">
                          {num(account.totalDebit)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {num(account.totalCredit)}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 text-right font-mono">{num(account.debitBalance)}</td>
                    <td className="px-4 py-3 text-right font-mono">{num(account.creditBalance)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/30 font-bold">
                <tr>
                  <td className="px-4 py-3" colSpan={3}>
                    {t('reports.totals')}
                  </td>
                  {data.hasOpening && (
                    <>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatCurrency(data.totals.openingDebit, 'TRY')}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatCurrency(data.totals.openingCredit, 'TRY')}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-3 text-right font-mono">
                    {formatCurrency(data.totals.debitTurnover, 'TRY')}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {formatCurrency(data.totals.creditTurnover, 'TRY')}
                  </td>
                  {data.hasOpening && (
                    <>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatCurrency(data.totals.totalDebit, 'TRY')}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatCurrency(data.totals.totalCredit, 'TRY')}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-3 text-right font-mono">
                    {formatCurrency(data.totals.debitBalance, 'TRY')}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {formatCurrency(data.totals.creditBalance, 'TRY')}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Profit & Loss Report
function ProfitLossReport() {
  const { t } = useTranslation('accounting');
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1);

  const [startDate, setStartDate] = useSessionState(
    'travel:accounting-reports:profitLoss:startDate',
    startOfYear.toISOString().split('T')[0] ?? ''
  );
  const [endDate, setEndDate] = useSessionState(
    'travel:accounting-reports:profitLoss:endDate',
    today.toISOString().split('T')[0] ?? ''
  );

  const { data, isLoading } = trpc.financialReports.getProfitAndLoss.useQuery({
    startDate: new Date(startDate || startOfYear),
    endDate: new Date(endDate || today),
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-card flex items-end gap-4 rounded-lg border p-4">
        <div className="space-y-2">
          <Label>{t('reports.startDate')}</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-48"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('reports.endDate')}</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-48"
          />
        </div>
      </div>

      {data && (
        <ReportExportButton onExport={() => downloadXlsx([...data.income.accounts.map((a) => ({ Bölüm: 'GELİR', Kod: a.code, 'Hesap Adı': a.name, Tutar: a.amount })), { Bölüm: 'GELİR', Kod: '', 'Hesap Adı': 'TOPLAM GELİR', Tutar: data.income.total }, ...data.expenses.accounts.map((a) => ({ Bölüm: 'GİDER', Kod: a.code, 'Hesap Adı': a.name, Tutar: a.amount })), { Bölüm: 'GİDER', Kod: '', 'Hesap Adı': 'TOPLAM GİDER', Tutar: data.expenses.total }, { Bölüm: '', Kod: '', 'Hesap Adı': 'NET KÂR / ZARAR', Tutar: data.netIncome }], 'Gelir Tablosu', `gelir-tablosu-${dateStamp()}.xlsx`)} />
      )}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : data ? (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Income Section */}
          <div className="bg-card rounded-xl border">
            <div className="border-b bg-green-50 p-4">
              <h3 className="text-lg font-semibold text-green-700">{t('reports.income')}</h3>
            </div>
            <div className="divide-y">
              {data.income.accounts.length === 0 ? (
                <p className="text-muted-foreground p-4 text-sm">{t('reports.noIncome')}</p>
              ) : (
                data.income.accounts.map((account) => (
                  <div key={account.id} className="flex justify-between p-4">
                    <div>
                      <span className="text-muted-foreground font-mono text-sm">
                        {account.code}
                      </span>
                      <span className="ml-2">{account.name}</span>
                    </div>
                    <span className="font-mono">{formatCurrency(account.amount, 'TRY')}</span>
                  </div>
                ))
              )}
            </div>
            <div className="flex justify-between border-t bg-green-50 p-4 font-bold">
              <span>{t('reports.totalIncome')}</span>
              <span className="font-mono text-green-700">
                {formatCurrency(data.income.total, 'TRY')}
              </span>
            </div>
          </div>

          {/* Expenses Section */}
          <div className="bg-card rounded-xl border">
            <div className="border-b bg-orange-50 p-4">
              <h3 className="text-lg font-semibold text-orange-700">{t('reports.expenses')}</h3>
            </div>
            <div className="divide-y">
              {data.expenses.accounts.length === 0 ? (
                <p className="text-muted-foreground p-4 text-sm">{t('reports.noExpenses')}</p>
              ) : (
                data.expenses.accounts.map((account) => (
                  <div key={account.id} className="flex justify-between p-4">
                    <div>
                      <span className="text-muted-foreground font-mono text-sm">
                        {account.code}
                      </span>
                      <span className="ml-2">{account.name}</span>
                    </div>
                    <span className="font-mono">{formatCurrency(account.amount, 'TRY')}</span>
                  </div>
                ))
              )}
            </div>
            <div className="flex justify-between border-t bg-orange-50 p-4 font-bold">
              <span>{t('reports.totalExpenses')}</span>
              <span className="font-mono text-orange-700">
                {formatCurrency(data.expenses.total, 'TRY')}
              </span>
            </div>
          </div>

          {/* Net Income */}
          <div className="lg:col-span-2">
            <div
              className={`rounded-xl border p-6 ${data.isProfitable ? 'bg-green-50' : 'bg-red-50'}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold">
                    {data.isProfitable ? t('reports.netIncome') : t('reports.netLoss')}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {startDate ? new Date(startDate).toLocaleDateString() : ''} -{' '}
                    {endDate ? new Date(endDate).toLocaleDateString() : ''}
                  </p>
                </div>
                <span
                  className={`font-mono text-3xl font-bold ${data.isProfitable ? 'text-green-700' : 'text-red-700'}`}
                >
                  {formatCurrency(Math.abs(data.netIncome), 'TRY')}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Balance Sheet Report
function BalanceSheetReport() {
  const { t } = useTranslation('accounting');
  const [asOfDate, setAsOfDate] = useSessionState(
    'travel:accounting-reports:balanceSheet:asOfDate',
    new Date().toISOString().split('T')[0] ?? ''
  );

  const { data, isLoading } = trpc.financialReports.getBalanceSheet.useQuery({
    asOfDate: new Date(asOfDate || new Date()),
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-card flex items-end gap-4 rounded-lg border p-4">
        <div className="space-y-2">
          <Label>{t('reports.asOfDate')}</Label>
          <Input
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            className="w-48"
          />
        </div>
      </div>

      {data && (
        <ReportExportButton onExport={() => downloadXlsx([...data.assets.accounts.map((a) => ({ Bölüm: 'AKTİF', Kod: a.code, 'Hesap Adı': a.name, Bakiye: a.balance })), { Bölüm: 'AKTİF', Kod: '', 'Hesap Adı': 'AKTİF TOPLAMI', Bakiye: data.assets.total }, ...data.liabilities.accounts.map((a) => ({ Bölüm: 'PASİF', Kod: a.code, 'Hesap Adı': a.name, Bakiye: a.balance })), { Bölüm: 'PASİF', Kod: '', 'Hesap Adı': 'YABANCI KAYNAKLAR TOPLAMI', Bakiye: data.liabilities.total }, ...data.equity.accounts.map((a) => ({ Bölüm: 'ÖZKAYNAK', Kod: a.code, 'Hesap Adı': a.name, Bakiye: a.balance })), { Bölüm: 'ÖZKAYNAK', Kod: '', 'Hesap Adı': 'DÖNEM KÂRI / ZARARI', Bakiye: data.equity.retainedEarnings }, { Bölüm: 'ÖZKAYNAK', Kod: '', 'Hesap Adı': 'ÖZKAYNAK TOPLAMI', Bakiye: data.equity.total }, { Bölüm: '', Kod: '', 'Hesap Adı': 'PASİF TOPLAMI', Bakiye: data.totalLiabilitiesAndEquity }], 'Bilanço', `bilanco-${dateStamp()}.xlsx`)} />
      )}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Assets */}
            <div className="bg-card rounded-xl border">
              <div className="border-b bg-blue-50 p-4">
                <h3 className="text-lg font-semibold text-blue-700">{t('reports.assets')}</h3>
              </div>
              <div className="divide-y">
                {data.assets.accounts.length === 0 ? (
                  <p className="text-muted-foreground p-4 text-sm">{t('reports.noAssets')}</p>
                ) : (
                  data.assets.accounts.map((account) => (
                    <div key={account.id} className="flex justify-between p-4">
                      <div>
                        <span className="text-muted-foreground font-mono text-sm">
                          {account.code}
                        </span>
                        <span className="ml-2">{account.name}</span>
                      </div>
                      <span className="font-mono">{formatCurrency(account.balance, 'TRY')}</span>
                    </div>
                  ))
                )}
              </div>
              <div className="flex justify-between border-t bg-blue-50 p-4 font-bold">
                <span>{t('reports.totalAssets')}</span>
                <span className="font-mono text-blue-700">
                  {formatCurrency(data.assets.total, 'TRY')}
                </span>
              </div>
            </div>

            {/* Liabilities & Equity */}
            <div className="space-y-6">
              {/* Liabilities */}
              <div className="bg-card rounded-xl border">
                <div className="border-b bg-red-50 p-4">
                  <h3 className="text-lg font-semibold text-red-700">{t('reports.liabilities')}</h3>
                </div>
                <div className="divide-y">
                  {data.liabilities.accounts.length === 0 ? (
                    <p className="text-muted-foreground p-4 text-sm">
                      {t('reports.noLiabilities')}
                    </p>
                  ) : (
                    data.liabilities.accounts.map((account) => (
                      <div key={account.id} className="flex justify-between p-4">
                        <div>
                          <span className="text-muted-foreground font-mono text-sm">
                            {account.code}
                          </span>
                          <span className="ml-2">{account.name}</span>
                        </div>
                        <span className="font-mono">{formatCurrency(account.balance, 'TRY')}</span>
                      </div>
                    ))
                  )}
                </div>
                <div className="flex justify-between border-t bg-red-50 p-4 font-bold">
                  <span>{t('reports.totalLiabilities')}</span>
                  <span className="font-mono text-red-700">
                    {formatCurrency(data.liabilities.total, 'TRY')}
                  </span>
                </div>
              </div>

              {/* Equity */}
              <div className="bg-card rounded-xl border">
                <div className="border-b bg-purple-50 p-4">
                  <h3 className="text-lg font-semibold text-purple-700">{t('reports.equity')}</h3>
                </div>
                <div className="divide-y">
                  {data.equity.accounts.map((account) => (
                    <div key={account.id} className="flex justify-between p-4">
                      <div>
                        <span className="text-muted-foreground font-mono text-sm">
                          {account.code}
                        </span>
                        <span className="ml-2">{account.name}</span>
                      </div>
                      <span className="font-mono">{formatCurrency(account.balance, 'TRY')}</span>
                    </div>
                  ))}
                  <div className="bg-muted/30 flex justify-between p-4">
                    <span className="italic">{t('reports.retainedEarnings')}</span>
                    <span className="font-mono">
                      {formatCurrency(data.equity.retainedEarnings, 'TRY')}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between border-t bg-purple-50 p-4 font-bold">
                  <span>{t('reports.totalEquity')}</span>
                  <span className="font-mono text-purple-700">
                    {formatCurrency(data.equity.total, 'TRY')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Balance Check */}
          <div className={`rounded-xl border p-6 ${data.isBalanced ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-muted-foreground text-sm">{t('reports.totalAssets')}</p>
                <p className="font-mono text-xl font-bold">
                  {formatCurrency(data.assets.total, 'TRY')}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-sm">=</p>
                <p className="text-xl font-bold">=</p>
              </div>
              <div>
                <p className="text-muted-foreground text-sm">
                  {t('reports.liabilitiesPlusEquity')}
                </p>
                <p className="font-mono text-xl font-bold">
                  {formatCurrency(data.totalLiabilitiesAndEquity, 'TRY')}
                </p>
              </div>
            </div>
            <div className="mt-4 text-center">
              {data.isBalanced ? (
                <span className="flex items-center justify-center gap-2 text-green-700">
                  <CheckCircle className="h-5 w-5" />
                  {t('reports.balanceSheetBalanced')}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  {t('reports.balanceSheetOutOfBalance')}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// AR Aging Report
function ARAgingReport() {
  const { t } = useTranslation('accounting');
  const [asOfDate, setAsOfDate] = useSessionState(
    'travel:accounting-reports:arAging:asOfDate',
    new Date().toISOString().split('T')[0] ?? ''
  );

  const { data, isLoading } = trpc.financialReports.getARaging.useQuery({
    asOfDate: new Date(asOfDate || new Date()),
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-card flex items-end gap-4 rounded-lg border p-4">
        <div className="space-y-2">
          <Label>{t('reports.asOfDate')}</Label>
          <Input
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            className="w-48"
          />
        </div>
      </div>

      {data && (
        <ReportExportButton onExport={() => downloadXlsx([...data.byCustomer.map((c) => ({ Müşteri: c.customer.name, 'E-posta': c.customer.email, 'Vadesi gelmemiş': c.current, '1-30 gün': c.days1to30, '31-60 gün': c.days31to60, '61-90 gün': c.days61to90, '90+ gün': c.over90, Toplam: c.total })), { Müşteri: 'TOPLAM', 'E-posta': '', 'Vadesi gelmemiş': data.summary.current, '1-30 gün': data.summary.days1to30, '31-60 gün': data.summary.days31to60, '61-90 gün': data.summary.days61to90, '90+ gün': data.summary.over90, Toplam: data.summary.total }], 'Alacak Yaşlandırma', `alacak-yaslandirma-${dateStamp()}.xlsx`)} />
      )}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : data ? (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid gap-4 md:grid-cols-6">
            {[
              {
                label: t('reports.aging.current'),
                value: data.summary.current,
                color: 'bg-green-100 text-green-700',
              },
              {
                label: t('reports.aging.days1to30'),
                value: data.summary.days1to30,
                color: 'bg-yellow-100 text-yellow-700',
              },
              {
                label: t('reports.aging.days31to60'),
                value: data.summary.days31to60,
                color: 'bg-orange-100 text-orange-700',
              },
              {
                label: t('reports.aging.days61to90'),
                value: data.summary.days61to90,
                color: 'bg-red-100 text-red-700',
              },
              {
                label: t('reports.aging.over90'),
                value: data.summary.over90,
                color: 'bg-red-200 text-red-800',
              },
              {
                label: t('reports.aging.total'),
                value: data.summary.total,
                color: 'bg-gray-100 text-gray-700',
              },
            ].map((item) => (
              <div key={item.label} className={`rounded-lg p-4 ${item.color}`}>
                <p className="text-sm font-medium">{item.label}</p>
                <p className="font-mono text-xl font-bold">{formatCurrency(item.value, 'TRY')}</p>
              </div>
            ))}
          </div>

          {/* By Customer */}
          <div className="bg-card rounded-xl border">
            <div className="border-b p-4">
              <h3 className="text-lg font-semibold">{t('reports.aging.byCustomer')}</h3>
            </div>
            {data.byCustomer.length === 0 ? (
              <p className="text-muted-foreground p-8 text-center">
                {t('reports.aging.noReceivables')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        {t('reports.aging.customer')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.aging.current')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.aging.days1to30')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.aging.days31to60')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.aging.days61to90')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.aging.over90')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.aging.total')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.byCustomer.map((row) => (
                      <tr key={row.customer.id} className="hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <p className="font-medium">{row.customer.name}</p>
                          <p className="text-muted-foreground text-xs">{row.customer.email}</p>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {row.current > 0 ? formatCurrency(row.current, 'TRY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {row.days1to30 > 0 ? formatCurrency(row.days1to30, 'TRY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {row.days31to60 > 0 ? formatCurrency(row.days31to60, 'TRY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {row.days61to90 > 0 ? formatCurrency(row.days61to90, 'TRY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-red-600">
                          {row.over90 > 0 ? formatCurrency(row.over90, 'TRY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-bold">
                          {formatCurrency(row.total, 'TRY')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// AP Aging Report
function APAgingReport() {
  const { t } = useTranslation('accounting');
  const [asOfDate, setAsOfDate] = useSessionState(
    'travel:accounting-reports:apAging:asOfDate',
    new Date().toISOString().split('T')[0] ?? ''
  );

  const { data, isLoading } = trpc.financialReports.getAPaging.useQuery({
    asOfDate: new Date(asOfDate || new Date()),
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-card flex items-end gap-4 rounded-lg border p-4">
        <div className="space-y-2">
          <Label>{t('reports.asOfDate')}</Label>
          <Input
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            className="w-48"
          />
        </div>
      </div>

      {data && (
        <ReportExportButton onExport={() => downloadXlsx([...data.byVendor.map((v) => ({ Tedarikçi: v.vendor.name, 'Vadesi gelmemiş': v.current, '1-30 gün': v.days1to30, '31-60 gün': v.days31to60, '61-90 gün': v.days61to90, '90+ gün': v.over90, Toplam: v.total })), { Tedarikçi: 'TOPLAM', 'Vadesi gelmemiş': data.summary.current, '1-30 gün': data.summary.days1to30, '31-60 gün': data.summary.days31to60, '61-90 gün': data.summary.days61to90, '90+ gün': data.summary.over90, Toplam: data.summary.total }], 'Borç Yaşlandırma', `borc-yaslandirma-${dateStamp()}.xlsx`)} />
      )}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : data ? (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid gap-4 md:grid-cols-6">
            {[
              {
                label: t('reports.aging.current'),
                value: data.summary.current,
                color: 'bg-green-100 text-green-700',
              },
              {
                label: t('reports.aging.days1to30'),
                value: data.summary.days1to30,
                color: 'bg-yellow-100 text-yellow-700',
              },
              {
                label: t('reports.aging.days31to60'),
                value: data.summary.days31to60,
                color: 'bg-orange-100 text-orange-700',
              },
              {
                label: t('reports.aging.days61to90'),
                value: data.summary.days61to90,
                color: 'bg-red-100 text-red-700',
              },
              {
                label: t('reports.aging.over90'),
                value: data.summary.over90,
                color: 'bg-red-200 text-red-800',
              },
              {
                label: t('reports.aging.total'),
                value: data.summary.total,
                color: 'bg-gray-100 text-gray-700',
              },
            ].map((item) => (
              <div key={item.label} className={`rounded-lg p-4 ${item.color}`}>
                <p className="text-sm font-medium">{item.label}</p>
                <p className="font-mono text-xl font-bold">{formatCurrency(item.value, 'TRY')}</p>
              </div>
            ))}
          </div>

          {/* By Vendor */}
          <div className="bg-card rounded-xl border">
            <div className="border-b p-4">
              <h3 className="text-lg font-semibold">{t('reports.aging.byVendor')}</h3>
            </div>
            {data.byVendor.length === 0 ? (
              <p className="text-muted-foreground p-8 text-center">
                {t('reports.aging.noPayables')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        {t('reports.aging.vendor')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.aging.current')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.aging.days1to30')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.aging.days31to60')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.aging.days61to90')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.aging.over90')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.aging.total')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.byVendor.map((row) => (
                      <tr key={row.vendor.id} className="hover:bg-muted/30">
                        <td className="px-4 py-3 font-medium">{row.vendor.name}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {row.current > 0 ? formatCurrency(row.current, 'TRY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {row.days1to30 > 0 ? formatCurrency(row.days1to30, 'TRY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {row.days31to60 > 0 ? formatCurrency(row.days31to60, 'TRY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {row.days61to90 > 0 ? formatCurrency(row.days61to90, 'TRY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-red-600">
                          {row.over90 > 0 ? formatCurrency(row.over90, 'TRY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-bold">
                          {formatCurrency(row.total, 'TRY')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Cash Flow Statement Report
function CashFlowReport() {
  const { t } = useTranslation('accounting');
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1);

  const [startDate, setStartDate] = useSessionState(
    'travel:accounting-reports:cashFlow:startDate',
    startOfYear.toISOString().split('T')[0] ?? ''
  );
  const [endDate, setEndDate] = useSessionState(
    'travel:accounting-reports:cashFlow:endDate',
    today.toISOString().split('T')[0] ?? ''
  );

  const { data, isLoading } = trpc.financialReports.getCashFlowStatement.useQuery({
    startDate: new Date(startDate || startOfYear),
    endDate: new Date(endDate || today),
    currency: 'TRY',
  });

  return (
    <div className="space-y-4">
      <div className="bg-card flex items-end gap-4 rounded-lg border p-4">
        <div className="space-y-2">
          <Label>{t('reports.startDate')}</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-48"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('reports.endDate')}</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-48"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="bg-card rounded-lg border p-4">
              <p className="text-muted-foreground text-sm">
                {t('reports.cashFlow.openingBalance')}
              </p>
              <p className="font-mono text-2xl font-bold">
                {formatCurrency(data.openingCashBalance, 'TRY')}
              </p>
            </div>
            <div className="bg-card rounded-lg border p-4">
              <p className="text-muted-foreground text-sm">{t('reports.cashFlow.netChange')}</p>
              <p
                className={`font-mono text-2xl font-bold ${data.netChangeInCash >= 0 ? 'text-green-600' : 'text-red-600'}`}
              >
                {data.netChangeInCash >= 0 ? '+' : ''}
                {formatCurrency(data.netChangeInCash, 'TRY')}
              </p>
            </div>
            <div className="bg-card rounded-lg border p-4">
              <p className="text-muted-foreground text-sm">
                {t('reports.cashFlow.closingBalance')}
              </p>
              <p className="font-mono text-2xl font-bold">
                {formatCurrency(data.closingCashBalance, 'TRY')}
              </p>
            </div>
          </div>

          <div className="bg-card rounded-xl border">
            <div className="border-b bg-blue-50 p-4">
              <h3 className="text-lg font-semibold text-blue-700">
                {t('reports.cashFlow.operatingActivities')}
              </h3>
            </div>
            <div className="divide-y">
              {data.operatingActivities.inflows.map((item, i) => (
                <div key={`in-${i}`} className="flex justify-between p-4">
                  <span className="text-green-600">+ {item.description}</span>
                  <span className="font-mono text-green-600">
                    {formatCurrency(item.amount, 'TRY')}
                  </span>
                </div>
              ))}
              {data.operatingActivities.outflows.map((item, i) => (
                <div key={`out-${i}`} className="flex justify-between p-4">
                  <span className="text-red-600">- {item.description}</span>
                  <span className="font-mono text-red-600">
                    ({formatCurrency(item.amount, 'TRY')})
                  </span>
                </div>
              ))}
            </div>
            <div className="flex justify-between border-t bg-blue-50 p-4 font-bold">
              <span>{t('reports.cashFlow.netCashFromOperating')}</span>
              <span
                className={`font-mono ${data.operatingActivities.netCashFromOperating >= 0 ? 'text-green-700' : 'text-red-700'}`}
              >
                {formatCurrency(data.operatingActivities.netCashFromOperating, 'TRY')}
              </span>
            </div>
          </div>

          <div className="bg-card rounded-xl border">
            <div className="border-b bg-purple-50 p-4">
              <h3 className="text-lg font-semibold text-purple-700">
                {t('reports.cashFlow.investingActivities')}
              </h3>
            </div>
            {data.investingActivities.inflows.length === 0 &&
            data.investingActivities.outflows.length === 0 ? (
              <p className="text-muted-foreground p-4 text-sm">
                {t('reports.cashFlow.noInvestingActivities')}
              </p>
            ) : (
              <div className="divide-y">
                {data.investingActivities.inflows.map((item, i) => (
                  <div key={`in-${i}`} className="flex justify-between p-4">
                    <span className="text-green-600">+ {item.description}</span>
                    <span className="font-mono text-green-600">
                      {formatCurrency(item.amount, 'TRY')}
                    </span>
                  </div>
                ))}
                {data.investingActivities.outflows.map((item, i) => (
                  <div key={`out-${i}`} className="flex justify-between p-4">
                    <span className="text-red-600">- {item.description}</span>
                    <span className="font-mono text-red-600">
                      ({formatCurrency(item.amount, 'TRY')})
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between border-t bg-purple-50 p-4 font-bold">
              <span>{t('reports.cashFlow.netCashFromInvesting')}</span>
              <span
                className={`font-mono ${data.investingActivities.netCashFromInvesting >= 0 ? 'text-green-700' : 'text-red-700'}`}
              >
                {formatCurrency(data.investingActivities.netCashFromInvesting, 'TRY')}
              </span>
            </div>
          </div>

          <div className="bg-card rounded-xl border">
            <div className="border-b bg-orange-50 p-4">
              <h3 className="text-lg font-semibold text-orange-700">
                {t('reports.cashFlow.financingActivities')}
              </h3>
            </div>
            {data.financingActivities.inflows.length === 0 &&
            data.financingActivities.outflows.length === 0 ? (
              <p className="text-muted-foreground p-4 text-sm">
                {t('reports.cashFlow.noFinancingActivities')}
              </p>
            ) : (
              <div className="divide-y">
                {data.financingActivities.inflows.map((item, i) => (
                  <div key={`in-${i}`} className="flex justify-between p-4">
                    <span className="text-green-600">+ {item.description}</span>
                    <span className="font-mono text-green-600">
                      {formatCurrency(item.amount, 'TRY')}
                    </span>
                  </div>
                ))}
                {data.financingActivities.outflows.map((item, i) => (
                  <div key={`out-${i}`} className="flex justify-between p-4">
                    <span className="text-red-600">- {item.description}</span>
                    <span className="font-mono text-red-600">
                      ({formatCurrency(item.amount, 'TRY')})
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between border-t bg-orange-50 p-4 font-bold">
              <span>{t('reports.cashFlow.netCashFromFinancing')}</span>
              <span
                className={`font-mono ${data.financingActivities.netCashFromFinancing >= 0 ? 'text-green-700' : 'text-red-700'}`}
              >
                {formatCurrency(data.financingActivities.netCashFromFinancing, 'TRY')}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// KDV (Turkish VAT) Report
function KDVReport() {
  const { t } = useTranslation('accounting');
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useSessionState(
    'travel:accounting-reports:kdv:startDate',
    startOfMonth.toISOString().split('T')[0] ?? ''
  );
  const [endDate, setEndDate] = useSessionState(
    'travel:accounting-reports:kdv:endDate',
    today.toISOString().split('T')[0] ?? ''
  );

  const { data, isLoading } = trpc.financialReports.getKDVReport.useQuery({
    startDate: new Date(startDate || startOfMonth),
    endDate: new Date(endDate || today),
  });

  return (
    <div className="space-y-4">
      <div className="bg-card flex items-end gap-4 rounded-lg border p-4">
        <div className="space-y-2">
          <Label>{t('reports.startDate')}</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-48"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('reports.endDate')}</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-48"
          />
        </div>
      </div>

      {data && (
        <ReportExportButton onExport={() => downloadXlsx([...Object.keys({ ...data.outputKDV.byRate, ...data.inputKDV.byRate }).sort((a, b) => Number(a) - Number(b)).map((rate) => ({ 'KDV Oranı': `%${rate}`, 'Hesaplanan KDV (391)': data.outputKDV.byRate[rate]?.amount ?? 0, 'İndirilecek KDV (191)': data.inputKDV.byRate[rate]?.amount ?? 0, Net: (data.outputKDV.byRate[rate]?.amount ?? 0) - (data.inputKDV.byRate[rate]?.amount ?? 0) })), { 'KDV Oranı': 'TOPLAM', 'Hesaplanan KDV (391)': data.outputKDV.total, 'İndirilecek KDV (191)': data.inputKDV.total, Net: data.netKDVPayable.amount }], 'KDV', `kdv-beyanname-${dateStamp()}.xlsx`)} />
      )}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border bg-green-50 p-4">
              <p className="text-sm text-green-700">{t('reports.kdv.outputKDV')}</p>
              <p className="font-mono text-2xl font-bold text-green-700">
                {formatCurrency(data.outputKDV.total, 'TRY')}
              </p>
              <p className="text-muted-foreground mt-1 text-xs">{data.outputKDV.description}</p>
            </div>
            <div className="rounded-lg border bg-blue-50 p-4">
              <p className="text-sm text-blue-700">{t('reports.kdv.inputKDV')}</p>
              <p className="font-mono text-2xl font-bold text-blue-700">
                {formatCurrency(data.inputKDV.total, 'TRY')}
              </p>
              <p className="text-muted-foreground mt-1 text-xs">{data.inputKDV.description}</p>
            </div>
            <div
              className={`rounded-lg border p-4 ${data.netKDVPayable.amount >= 0 ? 'bg-red-50' : 'bg-green-50'}`}
            >
              <p
                className={`text-sm ${data.netKDVPayable.amount >= 0 ? 'text-red-700' : 'text-green-700'}`}
              >
                {data.netKDVPayable.amount >= 0
                  ? t('reports.kdv.netPayable')
                  : t('reports.kdv.netReceivable')}
              </p>
              <p
                className={`font-mono text-2xl font-bold ${data.netKDVPayable.amount >= 0 ? 'text-red-700' : 'text-green-700'}`}
              >
                {formatCurrency(Math.abs(data.netKDVPayable.amount), 'TRY')}
              </p>
              <p className="text-muted-foreground mt-1 text-xs">{data.netKDVPayable.description}</p>
            </div>
          </div>

          <div className="bg-card rounded-xl border">
            <div className="border-b p-4">
              <h3 className="text-lg font-semibold">{t('reports.kdv.outputByRate')}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('reports.kdv.rate')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('reports.kdv.account')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('reports.kdv.amount')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {Object.entries(data.outputKDV.byRate).map(([rate, info]) => (
                    <tr key={rate} className="hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium">{rate}%</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-muted-foreground font-mono">{info.accountCode}</span>{' '}
                        <span className="ml-2">{info.accountName}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatCurrency(info.amount, 'TRY')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-card rounded-xl border">
            <div className="border-b p-4">
              <h3 className="text-lg font-semibold">{t('reports.kdv.inputByRate')}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('reports.kdv.rate')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('reports.kdv.account')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('reports.kdv.amount')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {Object.entries(data.inputKDV.byRate).map(([rate, info]) => (
                    <tr key={rate} className="hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium">{rate}%</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-muted-foreground font-mono">{info.accountCode}</span>{' '}
                        <span className="ml-2">{info.accountName}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatCurrency(info.amount, 'TRY')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Commission Report
function CommissionReport() {
  const { t } = useTranslation('accounting');
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useSessionState(
    'travel:accounting-reports:commission:startDate',
    startOfMonth.toISOString().split('T')[0] ?? ''
  );
  const [endDate, setEndDate] = useSessionState(
    'travel:accounting-reports:commission:endDate',
    today.toISOString().split('T')[0] ?? ''
  );

  const { data, isLoading } = trpc.financialReports.getCommissionReport.useQuery({
    startDate: new Date(startDate || startOfMonth),
    endDate: new Date(endDate || today),
  });

  return (
    <div className="space-y-4">
      <div className="bg-card flex items-end gap-4 rounded-lg border p-4">
        <div className="space-y-2">
          <Label>{t('reports.startDate')}</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-48"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('reports.endDate')}</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-48"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-5">
            <div className="bg-card rounded-lg border p-4">
              <p className="text-muted-foreground text-sm">{t('reports.commission.total')}</p>
              <p className="text-2xl font-bold">{data.summary.totalCommissions}</p>
            </div>
            <div className="rounded-lg border bg-yellow-50 p-4">
              <p className="text-sm text-yellow-700">{t('reports.commission.pending')}</p>
              <p className="font-mono text-2xl font-bold text-yellow-700">
                {formatCurrency(data.summary.pending, 'TRY')}
              </p>
            </div>
            <div className="rounded-lg border bg-blue-50 p-4">
              <p className="text-sm text-blue-700">{t('reports.commission.approved')}</p>
              <p className="font-mono text-2xl font-bold text-blue-700">
                {formatCurrency(data.summary.approved, 'TRY')}
              </p>
            </div>
            <div className="rounded-lg border bg-green-50 p-4">
              <p className="text-sm text-green-700">{t('reports.commission.paid')}</p>
              <p className="font-mono text-2xl font-bold text-green-700">
                {formatCurrency(data.summary.paid, 'TRY')}
              </p>
            </div>
            <div className="rounded-lg border bg-orange-50 p-4">
              <p className="text-sm text-orange-700">{t('reports.commission.netPayable')}</p>
              <p className="font-mono text-2xl font-bold text-orange-700">
                {formatCurrency(data.summary.netPayable, 'TRY')}
              </p>
            </div>
          </div>

          <div className="bg-card rounded-xl border">
            <div className="border-b p-4">
              <h3 className="text-lg font-semibold">{t('reports.commission.byPartner')}</h3>
            </div>
            {data.byPartner.length === 0 ? (
              <p className="text-muted-foreground p-8 text-center">
                {t('reports.commission.noData')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        {t('reports.commission.partner')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.commission.pending')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.commission.approved')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.commission.paid')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.commission.total')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.byPartner.map((row) => (
                      <tr key={row.partner.id} className="hover:bg-muted/30">
                        <td className="px-4 py-3 font-medium">{row.partner.companyName}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {row.pending > 0 ? formatCurrency(row.pending, 'TRY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {row.approved > 0 ? formatCurrency(row.approved, 'TRY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {row.paid > 0 ? formatCurrency(row.paid, 'TRY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-bold">
                          {formatCurrency(row.total, 'TRY')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-card rounded-xl border">
            <div className="border-b p-4">
              <h3 className="text-lg font-semibold">{t('reports.commission.recent')}</h3>
            </div>
            {data.recentCommissions.length === 0 ? (
              <p className="text-muted-foreground p-8 text-center">
                {t('reports.commission.noRecent')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        {t('reports.commission.partner')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        {t('reports.commission.booking')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        {t('reports.commission.type')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.commission.rate')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.commission.amount')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        {t('reports.commission.status')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.recentCommissions.map((comm) => (
                      <tr key={comm.id} className="hover:bg-muted/30">
                        <td className="px-4 py-3 font-medium">{comm.partnerName}</td>
                        <td className="px-4 py-3 font-mono text-sm">{comm.bookingNumber}</td>
                        <td className="px-4 py-3 text-sm">
                          {comm.type === 'PARTNER_PAYABLE'
                            ? t('reports.commission.payable')
                            : t('reports.commission.receivable')}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">{comm.rate}%</td>
                        <td className="px-4 py-3 text-right font-mono">
                          {formatCurrency(comm.commissionAmount, comm.currency)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 text-xs font-medium ${comm.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' : comm.status === 'APPROVED' ? 'bg-blue-100 text-blue-700' : comm.status === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                          >
                            {comm.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Profit Margin Report
function ProfitMarginReport() {
  const { t } = useTranslation('accounting');
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1);
  const [startDate, setStartDate] = useSessionState(
    'travel:accounting-reports:profitMargin:startDate',
    startOfYear.toISOString().split('T')[0] ?? ''
  );
  const [endDate, setEndDate] = useSessionState(
    'travel:accounting-reports:profitMargin:endDate',
    today.toISOString().split('T')[0] ?? ''
  );

  const { data, isLoading } = trpc.financialReports.getProfitMarginReport.useQuery({
    startDate: new Date(startDate || startOfYear),
    endDate: new Date(endDate || today),
  });

  return (
    <div className="space-y-4">
      <div className="bg-card flex items-end gap-4 rounded-lg border p-4">
        <div className="space-y-2">
          <Label>{t('reports.startDate')}</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-48"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('reports.endDate')}</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-48"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="bg-card rounded-lg border p-4">
              <p className="text-muted-foreground text-sm">
                {t('reports.profitMargin.totalBookings')}
              </p>
              <p className="text-2xl font-bold">{data.summary.totalBookings}</p>
            </div>
            <div className="rounded-lg border bg-green-50 p-4">
              <p className="text-sm text-green-700">{t('reports.profitMargin.totalRevenue')}</p>
              <p className="font-mono text-2xl font-bold text-green-700">
                {formatCurrency(data.summary.totalRevenue, 'TRY')}
              </p>
            </div>
            <div className="rounded-lg border bg-orange-50 p-4">
              <p className="text-sm text-orange-700">{t('reports.profitMargin.totalCosts')}</p>
              <p className="font-mono text-2xl font-bold text-orange-700">
                {formatCurrency(
                  data.summary.totalCOGS +
                    data.summary.totalExpenses +
                    data.summary.totalCommissions,
                  'TRY'
                )}
              </p>
            </div>
            <div
              className={`rounded-lg border p-4 ${data.summary.overallMarginPercent >= 20 ? 'bg-green-50' : data.summary.overallMarginPercent >= 0 ? 'bg-yellow-50' : 'bg-red-50'}`}
            >
              <p className="text-muted-foreground text-sm">
                {t('reports.profitMargin.overallMargin')}
              </p>
              <p
                className={`text-2xl font-bold ${data.summary.overallMarginPercent >= 20 ? 'text-green-700' : data.summary.overallMarginPercent >= 0 ? 'text-yellow-700' : 'text-red-700'}`}
              >
                {data.summary.overallMarginPercent}%
              </p>
            </div>
          </div>

          <div className="bg-card rounded-xl border p-6">
            <h3 className="mb-4 text-lg font-semibold">{t('reports.profitMargin.distribution')}</h3>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="rounded-lg bg-red-100 p-4 text-center">
                <p className="text-3xl font-bold text-red-700">{data.distribution.negative}</p>
                <p className="text-sm text-red-700">{t('reports.profitMargin.negative')}</p>
              </div>
              <div className="rounded-lg bg-yellow-100 p-4 text-center">
                <p className="text-3xl font-bold text-yellow-700">{data.distribution.low}</p>
                <p className="text-sm text-yellow-700">{t('reports.profitMargin.low')}</p>
              </div>
              <div className="rounded-lg bg-blue-100 p-4 text-center">
                <p className="text-3xl font-bold text-blue-700">{data.distribution.medium}</p>
                <p className="text-sm text-blue-700">{t('reports.profitMargin.medium')}</p>
              </div>
              <div className="rounded-lg bg-green-100 p-4 text-center">
                <p className="text-3xl font-bold text-green-700">{data.distribution.high}</p>
                <p className="text-sm text-green-700">{t('reports.profitMargin.high')}</p>
              </div>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="bg-card rounded-xl border">
              <div className="border-b bg-green-50 p-4">
                <h3 className="text-lg font-semibold text-green-700">
                  {t('reports.profitMargin.topPerformers')}
                </h3>
              </div>
              <div className="divide-y">
                {data.topPerformers.map((b) => (
                  <div key={b.bookingId} className="flex justify-between p-4">
                    <div>
                      <p className="font-medium">{b.bookingNumber}</p>
                      <p className="text-muted-foreground text-sm">{b.customer}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">{b.marginPercent}%</p>
                      <p className="font-mono text-sm">{formatCurrency(b.grossProfit, 'TRY')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-card rounded-xl border">
              <div className="border-b bg-red-50 p-4">
                <h3 className="text-lg font-semibold text-red-700">
                  {t('reports.profitMargin.bottomPerformers')}
                </h3>
              </div>
              <div className="divide-y">
                {data.bottomPerformers.map((b) => (
                  <div key={b.bookingId} className="flex justify-between p-4">
                    <div>
                      <p className="font-medium">{b.bookingNumber}</p>
                      <p className="text-muted-foreground text-sm">{b.customer}</p>
                    </div>
                    <div className="text-right">
                      <p
                        className={`font-bold ${b.marginPercent < 0 ? 'text-red-600' : 'text-yellow-600'}`}
                      >
                        {b.marginPercent}%
                      </p>
                      <p className="font-mono text-sm">{formatCurrency(b.grossProfit, 'TRY')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl border">
            <div className="border-b p-4">
              <h3 className="text-lg font-semibold">{t('reports.profitMargin.allBookings')}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('reports.profitMargin.booking')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('reports.profitMargin.customer')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('reports.profitMargin.revenue')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('reports.profitMargin.costs')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('reports.profitMargin.profit')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('reports.profitMargin.margin')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.allBookings.slice(0, 20).map((b) => (
                    <tr key={b.bookingId} className="hover:bg-muted/30">
                      <td className="px-4 py-3 font-mono text-sm">{b.bookingNumber}</td>
                      <td className="px-4 py-3">{b.customer}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        {formatCurrency(b.revenue, 'TRY')}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        {formatCurrency(b.totalCosts, 'TRY')}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        <span className={b.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(b.grossProfit, 'TRY')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={`px-2 py-0.5 text-xs font-medium ${b.marginPercent >= 40 ? 'bg-green-100 text-green-700' : b.marginPercent >= 20 ? 'bg-blue-100 text-blue-700' : b.marginPercent >= 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}
                        >
                          {b.marginPercent}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Bank Reconciliation Report
function BankReconciliationReport() {
  const { t } = useTranslation('accounting');
  const [asOfDate, setAsOfDate] = useSessionState(
    'travel:accounting-reports:bankRecon:asOfDate',
    new Date().toISOString().split('T')[0] ?? ''
  );
  const [selectedAccountId, setSelectedAccountId] = useSessionState<string>(
    'travel:accounting-reports:bankRecon:selectedAccountId',
    ''
  );

  const { data: accounts } = trpc.account.list.useQuery({ type: 'ASSET', isActive: true });
  const bankAccounts =
    accounts?.accounts.filter((a) => a.code.startsWith('100') || a.code.startsWith('102')) || [];

  const { data, isLoading } = trpc.financialReports.getBankReconciliationReport.useQuery(
    { accountId: selectedAccountId, asOfDate: new Date(asOfDate || new Date()) },
    { enabled: !!selectedAccountId }
  );

  return (
    <div className="space-y-4">
      <div className="bg-card flex items-end gap-4 rounded-lg border p-4">
        <div className="space-y-2">
          <Label>{t('reports.bankRecon.accountLabel')}</Label>
          <Combobox
            options={bankAccounts.map((a) => ({
              value: a.id,
              label: `${a.code} - ${a.name}`,
            }))}
            value={selectedAccountId}
            onValueChange={setSelectedAccountId}
            placeholder={t('reports.bankRecon.selectAccount')}
            searchPlaceholder={t('reports.bankRecon.searchAccounts')}
            className="w-64"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('reports.asOfDate')}</Label>
          <Input
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            className="w-48"
          />
        </div>
      </div>

      {!selectedAccountId ? (
        <div className="bg-card rounded-xl border py-12 text-center">
          <Scale className="text-muted-foreground/50 mx-auto h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('reports.bankRecon.selectPromptTitle')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('reports.bankRecon.selectPromptDescription')}
          </p>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="bg-card rounded-lg border p-4">
              <p className="text-muted-foreground text-sm">{t('reports.bankRecon.bookBalance')}</p>
              <p className="font-mono text-2xl font-bold">
                {formatCurrency(data.bookBalance, 'TRY')}
              </p>
            </div>
            <div className="bg-card rounded-lg border p-4">
              <p className="text-muted-foreground text-sm">
                {t('reports.bankRecon.statementBalance')}
              </p>
              <p className="font-mono text-2xl font-bold">
                {formatCurrency(data.statementBalance, 'TRY')}
              </p>
            </div>
            <div className="bg-card rounded-lg border p-4">
              <p className="text-muted-foreground text-sm">{t('reports.bankRecon.adjustedBank')}</p>
              <p className="font-mono text-2xl font-bold">
                {formatCurrency(data.reconciliation.adjustedBankBalance, 'TRY')}
              </p>
            </div>
            <div
              className={`rounded-lg border p-4 ${data.isReconciled ? 'bg-green-50' : 'bg-red-50'}`}
            >
              <p className="text-muted-foreground text-sm">{t('reports.bankRecon.difference')}</p>
              <p
                className={`font-mono text-2xl font-bold ${data.isReconciled ? 'text-green-700' : 'text-red-700'}`}
              >
                {formatCurrency(data.difference, 'TRY')}
              </p>
              {data.isReconciled ? (
                <span className="mt-1 flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle className="h-3 w-3" />
                  {t('reports.bankRecon.reconciled')}
                </span>
              ) : (
                <span className="mt-1 flex items-center gap-1 text-xs text-red-600">
                  <AlertCircle className="h-3 w-3" />
                  {t('reports.bankRecon.notReconciled')}
                </span>
              )}
            </div>
          </div>

          {data.warnings.length > 0 && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <h4 className="mb-2 font-medium text-yellow-700">
                {t('reports.bankRecon.warnings')}
              </h4>
              <ul className="list-inside list-disc text-sm text-yellow-700">
                {data.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-card rounded-xl border">
            <div className="flex justify-between border-b bg-green-50 p-4">
              <h3 className="text-lg font-semibold text-green-700">
                {t('reports.bankRecon.depositsInTransit')}
              </h3>
              <span className="font-mono font-bold text-green-700">
                {formatCurrency(data.reconciliation.depositsInTransit.total, 'TRY')}
              </span>
            </div>
            {data.reconciliation.depositsInTransit.items.length === 0 ? (
              <p className="text-muted-foreground p-4 text-sm">
                {t('reports.bankRecon.noDepositsInTransit')}
              </p>
            ) : (
              <div className="divide-y">
                {data.reconciliation.depositsInTransit.items.map((item, i) => (
                  <div key={i} className="flex justify-between p-4">
                    <div>
                      <p className="font-medium">{item.description}</p>
                      <p className="text-muted-foreground text-sm">
                        {new Date(item.date).toLocaleDateString()} • {item.reference}
                      </p>
                    </div>
                    <span className="font-mono">{formatCurrency(item.amount, 'TRY')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-card rounded-xl border">
            <div className="flex justify-between border-b bg-red-50 p-4">
              <h3 className="text-lg font-semibold text-red-700">
                {t('reports.bankRecon.outstandingChecks')}
              </h3>
              <span className="font-mono font-bold text-red-700">
                ({formatCurrency(data.reconciliation.outstandingChecks.total, 'TRY')})
              </span>
            </div>
            {data.reconciliation.outstandingChecks.items.length === 0 ? (
              <p className="text-muted-foreground p-4 text-sm">
                {t('reports.bankRecon.noOutstandingChecks')}
              </p>
            ) : (
              <div className="divide-y">
                {data.reconciliation.outstandingChecks.items.map((item, i) => (
                  <div key={i} className="flex justify-between p-4">
                    <div>
                      <p className="font-medium">{item.description}</p>
                      <p className="text-muted-foreground text-sm">
                        {new Date(item.date).toLocaleDateString()} • {item.reference}
                      </p>
                    </div>
                    <span className="font-mono text-red-600">
                      ({formatCurrency(item.amount, 'TRY')})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {data.reconciliation.unreconciledBookItems.length > 0 && (
            <div className="bg-card rounded-xl border">
              <div className="border-b p-4">
                <h3 className="text-lg font-semibold">
                  {t('reports.bankRecon.unreconciledEntries')}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        {t('reports.bankRecon.date')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        {t('reports.bankRecon.entryNumber')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        {t('reports.bankRecon.description')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.bankRecon.debit')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('reports.bankRecon.credit')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.reconciliation.unreconciledBookItems.slice(0, 20).map((item, i) => (
                      <tr key={i} className="hover:bg-muted/30">
                        <td className="px-4 py-3 text-sm">
                          {new Date(item.date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm">{item.entryNumber}</td>
                        <td className="px-4 py-3">{item.description}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {item.debit > 0 ? formatCurrency(item.debit, 'TRY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {item.credit > 0 ? formatCurrency(item.credit, 'TRY') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
